<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import type { Size } from '../types/size';
import Icon from './Icon.vue';

interface Props {
	/** Stable `data-testid` for E2E selectors. Rendered on the interactive element, not the wrapper. */
	testId?: string;
	label?: string;
	icon: string;
	severity?: 'primary' | 'basic' | 'light' | 'error';
	variant?: 'empty';
	size?: Extract<Size, 'xs' | 'sm' | 'md' | 'lg'>;
	isCircle?: boolean;
	isActive?: boolean;
	to?: RouteLocationRaw;
	target?: string;
	external?: boolean;
	loading?: boolean;
	disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	severity: 'primary',
	variant: 'empty',
	size: 'md',
});
</script>

<template>
	<button
		:data-testid="props.testId"
		type="button"
		class="btn-icon"
		:class="[{
			[`btn-icon--${props.severity}`]: props.severity,
			[`btn-icon--${props.variant}`]: props.variant,
			[`btn-icon--${props.size}`]: props.size,
			'btn-icon--is-circle': props.isCircle,
			'btn-icon--is-active': props.isActive,
			'btn-icon--loading': props.loading,
		}]"
		:disabled="props.disabled"
		v-if="!to"
	>
		<Icon :name="props.icon" />
		<span class="aria:sr" v-if="props.label">{{ props.label }}</span>
		<span class="btn-icon__loading">
			<Icon name="circle-notch" />
		</span>
	</button>

	<NuxtLink
		:data-testid="props.testId"
		class="btn-icon"
		:class="[{
			[`btn-icon--${props.severity}`]: props.severity,
			[`btn-icon--${props.variant}`]: props.variant,
			[`btn-icon--${props.size}`]: props.size,
			'btn-icon--is-circle': props.isCircle,
			'btn-icon--is-active': props.isActive,
			'btn-icon--loading': props.loading,
		}]"
		:to="props.to"
		:target="props.target"
		:external="props.external"
		:rel="props.target ? 'noopener noreferrer' : null"
		v-else
	>
		<Icon :name="props.icon" />
		<span class="aria:sr" v-if="props.label">{{ props.label }}</span>
	</NuxtLink>
</template>
